/**
 * `2e` — Oportunidades, gestão e curadoria.
 *
 * O wireframe pede um grid "como se fosse uma planilha mesmo com filtros em cada
 * coluna". É a tela mais densa do sistema: 10 colunas, filtro em cada uma,
 * visibilidade de coluna configurável e edição por linha.
 *
 * 🔒 **Duas regras moram aqui e não podem sair:**
 *  - **BR-01** — o toggle "Visível cliente" é o ATO que expõe a oportunidade ao
 *    Cliente. Chama endpoint próprio (`PUT .../visibilidade`), e não o PATCH
 *    genérico, porque é auditado. Só aparece com permissão de escrita.
 *  - **BR-20** — o filtro de status já nasce excluindo `arquivada`, e essa
 *    decisão vem do CATÁLOGO (`incluirNoFiltroPadrao`), não de um
 *    `status !== 'arquivada'` escrito aqui.
 *
 * 🔒 **Nenhuma linha é escondida no cliente.** O servidor devolve o conjunto já
 * filtrado. Se esta tela precisasse esconder algo, a regra estaria no lugar
 * errado.
 */
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import type { Oportunidade } from '@/api/contracts/oportunidade'
import {
  useAlternarVisibilidade,
  useCatalogoDeStatus,
  useOportunidades,
  type FiltrosDeOportunidade,
} from '@/api/queries/finops'
import { EsqueletoDeTabela } from '@/components/esqueleto'
import { EstadoDeErro, EstadoVazio } from '@/components/estados'
import { BadgeDeStatus, Botao, Chip, Switch } from '@/components/primitivos'
import { formatarData, formatarUsd } from '@/format'
import { useClienteAtual } from '@/tenant/useClienteAtual'

/** As 10 colunas do shot `2e`, na ordem desenhada. */
const COLUNAS = [
  { id: 'dataLancamento', rotulo: 'Lançada em', filtravel: true },
  { id: 'origem', rotulo: 'Origem', filtravel: true },
  { id: 'recurso', rotulo: 'Recurso / descrição', filtravel: true },
  { id: 'accountId', rotulo: 'Conta', filtravel: true },
  { id: 'servicoAws', rotulo: 'Serviço', filtravel: true },
  { id: 'status', rotulo: 'Status', filtravel: true },
  { id: 'economiaMensalPotencial', rotulo: 'Economia pot./mês', filtravel: false },
  { id: 'dataImplementacao', rotulo: 'Implementada em', filtravel: false },
  { id: 'visibilidadeCliente', rotulo: 'Visível cliente', filtravel: false },
  { id: 'acoes', rotulo: '', filtravel: false },
] as const

type IdDeColuna = (typeof COLUNAS)[number]['id']

export function Oportunidades() {
  const idCliente = useClienteAtual()
  const navegar = useNavigate()

  const [filtros, setFiltros] = useState<FiltrosDeOportunidade>({})
  const [colunasOcultas, setColunasOcultas] = useState<Set<IdDeColuna>>(new Set())

  const consulta = useOportunidades(idCliente, { ...filtros, tamanho: 200 })
  const catalogo = useCatalogoDeStatus(idCliente)
  const alternar = useAlternarVisibilidade(idCliente)

  const podeEditar = consulta.data?.podeEditar ?? false

  /**
   * Os valores de cada filtro de coluna saem das linhas JÁ CARREGADAS.
   *
   * ⚠️ Consequência honesta: com paginação, o menu só oferece o que está na
   * página. Aceitável enquanto o volume é de milhares por tenant e a tela pede
   * 200 de uma vez; quando passar disso, estes valores viram `GET
   * /finops/custos/dimensoes` — que já existe para a `2c`.
   */
  const valoresPorColuna = useMemo(() => {
    const linhas = consulta.data?.itens ?? []
    const coletar = (extrair: (o: Oportunidade) => string | null) =>
      [...new Set(linhas.map(extrair).filter((v): v is string => Boolean(v)))].sort()
    return {
      origem: coletar((o) => o.origem),
      accountId: coletar((o) => o.accountId),
      servicoAws: coletar((o) => o.servicoAws),
      status: coletar((o) => o.status),
    }
  }, [consulta.data])

  const rotuloDoStatus = (status: string) =>
    catalogo.data?.itens.find((s) => s.idStatus === status)?.rotulo ?? status

  const filtrosAtivos = Object.entries(filtros).filter(
    ([, valor]) => Array.isArray(valor) && valor.length > 0,
  ) as [keyof FiltrosDeOportunidade, string[]][]

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h2 className="type-h4 text-text-primary">Oportunidades</h2>
          {consulta.data && (
            <span className="type-sm text-text-muted">
              {consulta.data.total} {consulta.data.total === 1 ? 'oportunidade' : 'oportunidades'}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <SeletorDeColunas ocultas={colunasOcultas} aoMudar={setColunasOcultas} />
          {/* Só com permissão de escrita — e o servidor recusa de qualquer forma
              (BR-17): esconder o botão é UX, não a fronteira de segurança. */}
          {podeEditar && (
            <Botao variante="accent" icone="plus" onClick={() => navegar('nova')}>
              lançar oportunidade
            </Botao>
          )}
        </div>
      </div>

      {filtrosAtivos.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {filtrosAtivos.map(([campo, valores]) => (
            <Chip
              key={campo}
              rotuloDeRemocao={`remover filtro de ${campo}`}
              aoRemover={() => setFiltros((atual) => ({ ...atual, [campo]: [] }))}
            >
              {campo}: {valores.join(', ')}
            </Chip>
          ))}
          <button
            type="button"
            className="type-sm text-text-link hover:text-iox-teal"
            onClick={() => setFiltros({})}
          >
            limpar tudo
          </button>
        </div>
      )}

      {/* As colunas VISÍVEIS, e não as dez do catálogo: o seletor de colunas
          já rodou, então o esqueleto reserva a largura que a tabela vai ter. */}
      {consulta.isPending && (
        <EsqueletoDeTabela
          rotulo="Carregando oportunidades…"
          colunas={COLUNAS.length - colunasOcultas.size}
        />
      )}
      {consulta.isError && (
        <EstadoDeErro erro={consulta.error} aoTentarNovamente={() => void consulta.refetch()} />
      )}

      {consulta.data && consulta.data.itens.length === 0 && (
        <EstadoVazio
          titulo="Nenhuma oportunidade neste filtro."
          descricao={
            filtrosAtivos.length > 0
              ? 'Limpe os filtros para ver o conjunto completo. Arquivadas ficam de fora por padrão.'
              : 'As oportunidades chegam da aba Discovery, promovidas pelo arquiteto, ou por lançamento manual.'
          }
        />
      )}

      {consulta.data && consulta.data.itens.length > 0 && (
        <div className="border-border-subtle bg-surface-card rounded-card overflow-x-auto border">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-border-subtle border-b">
                {COLUNAS.filter((c) => !colunasOcultas.has(c.id)).map((coluna) => (
                  <th
                    key={coluna.id}
                    scope="col"
                    className="text-text-muted px-3 py-3 text-left type-eyebrow whitespace-nowrap"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {coluna.rotulo}
                      {coluna.filtravel && coluna.id in valoresPorColuna && (
                        <FiltroDeColuna
                          rotulo={coluna.rotulo}
                          valores={valoresPorColuna[coluna.id as keyof typeof valoresPorColuna]}
                          selecionados={
                            (filtros[coluna.id as keyof FiltrosDeOportunidade] ?? []) as string[]
                          }
                          rotularValor={coluna.id === 'status' ? rotuloDoStatus : undefined}
                          aoMudar={(selecionados) =>
                            setFiltros((atual) => ({ ...atual, [coluna.id]: selecionados }))
                          }
                        />
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {consulta.data.itens.map((o, indice) => (
                <Linha
                  key={o.idOportunidade}
                  oportunidade={o}
                  zebra={indice % 2 === 1}
                  ocultas={colunasOcultas}
                  podeEditar={podeEditar}
                  rotuloDoStatus={rotuloDoStatus}
                  alternando={alternar.isPending}
                  aoAlternar={(visibilidadeCliente) =>
                    alternar.mutate({ idOportunidade: o.idOportunidade, visibilidadeCliente })
                  }
                  aoEditar={() => navegar(o.idOportunidade)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {alternar.isError && <EstadoDeErro erro={alternar.error} />}

      <p className="text-text-muted type-sm">
        Ciclo de status: rascunho → nova → proposta → priorizada → implementada · arquivada a
        qualquer momento. O filtro de status exclui arquivadas por padrão. As listas de status e de
        serviços AWS são editáveis na Administração.
      </p>
    </section>
  )
}

/** Uma linha do grid. Extraída para o corpo da tabela caber numa tela. */
function Linha({
  oportunidade: o,
  zebra,
  ocultas,
  podeEditar,
  rotuloDoStatus,
  alternando,
  aoAlternar,
  aoEditar,
}: {
  oportunidade: Oportunidade
  zebra: boolean
  ocultas: Set<IdDeColuna>
  podeEditar: boolean
  rotuloDoStatus: (status: string) => string
  alternando: boolean
  aoAlternar: (proximo: boolean) => void
  aoEditar: () => void
}) {
  const celula = 'text-text-body px-3 py-3 type-sm whitespace-nowrap'
  return (
    <tr className={`border-border-subtle border-b last:border-b-0 ${zebra ? 'bg-gray-25' : ''}`}>
      {!ocultas.has('dataLancamento') && (
        <td className={celula}>{formatarData(o.dataLancamento)}</td>
      )}
      {!ocultas.has('origem') && (
        <td className={celula}>
          <span className="inline-flex items-center gap-1.5">
            <i
              className={`ph ph-${o.origem === 'automatico' ? 'robot' : 'user'} text-icon-default`}
              aria-hidden="true"
            />
            {o.origem === 'automatico' ? 'autom.' : 'manual'}
          </span>
        </td>
      )}
      {!ocultas.has('recurso') && (
        <td className="max-w-md px-3 py-3">
          <span className="text-text-primary block type-sm font-semibold">
            {o.recurso || <span className="text-text-muted">(sem recurso)</span>}
          </span>
          <span className="text-text-muted block truncate type-sm">{o.descricao}</span>
        </td>
      )}
      {!ocultas.has('accountId') && <td className={celula}>{o.accountId || '—'}</td>}
      {!ocultas.has('servicoAws') && <td className={celula}>{o.servicoAws || '—'}</td>}
      {!ocultas.has('status') && (
        <td className="px-3 py-3">
          <BadgeDeStatus status={o.status} rotulo={rotuloDoStatus(o.status)} />
        </td>
      )}
      {!ocultas.has('economiaMensalPotencial') && (
        <td className="text-text-primary px-3 py-3 text-right type-sm font-semibold whitespace-nowrap">
          {/* `—` e não "US$ 0,00": um rascunho sem custo não tem economia zero,
              tem economia DESCONHECIDA — e mostrar zero seria afirmar algo falso. */}
          {o.economiaMensalPotencial === null ? '—' : formatarUsd(o.economiaMensalPotencial)}
        </td>
      )}
      {!ocultas.has('dataImplementacao') && (
        <td className={celula}>
          {o.dataImplementacao ? (
            formatarData(o.dataImplementacao)
          ) : (
            <span className="text-text-muted">—</span>
          )}
        </td>
      )}
      {!ocultas.has('visibilidadeCliente') && (
        <td className="px-3 py-3">
          {/* 🔒 O ATO do BR-01. Sem permissão de escrita vira só um indicador —
              e o servidor recusaria a chamada de qualquer forma (BR-17). */}
          {podeEditar ? (
            <Switch
              ligado={o.visibilidadeCliente}
              rotulo={`Visível ao cliente — ${o.codigo}`}
              ocupado={alternando}
              aoAlternar={aoAlternar}
            />
          ) : (
            <span className="type-sm text-text-muted">{o.visibilidadeCliente ? 'sim' : 'não'}</span>
          )}
        </td>
      )}
      {!ocultas.has('acoes') && (
        <td className="px-3 py-3 text-right">
          <button
            type="button"
            aria-label={`Editar ${o.codigo}`}
            className="text-icon-default hover:text-iox-teal duration-fast"
            onClick={aoEditar}
          >
            <i className="ph ph-pencil-simple" aria-hidden="true" />
          </button>
        </td>
      )}
    </tr>
  )
}

/**
 * O menu de funil de uma coluna.
 *
 * `<details>` nativo, e não um popover com portal: ele já fecha com Escape, é
 * navegável por teclado e não precisa de gerenciamento de foco. O gatilho para
 * trocar por Radix é o dia em que a lista precisar de busca — aí sim o nativo
 * não resolve.
 *
 * O ícone vem preenchido (`ph-fill`) quando há filtro ativo, exatamente como o
 * shot `2e` desenha o funil de Status.
 */
function FiltroDeColuna({
  rotulo,
  valores,
  selecionados,
  aoMudar,
  rotularValor,
}: {
  rotulo: string
  valores: string[]
  selecionados: string[]
  aoMudar: (selecionados: string[]) => void
  rotularValor?: ((valor: string) => string) | undefined
}) {
  const ativo = selecionados.length > 0
  return (
    <details className="relative inline-block">
      <summary
        className="cursor-pointer list-none"
        aria-label={`Filtrar por ${rotulo}${ativo ? ` (${selecionados.length} selecionados)` : ''}`}
      >
        <i
          className={`ph ${ativo ? 'ph-fill ph-funnel text-iox-navy' : 'ph-funnel text-icon-muted'}`}
          aria-hidden="true"
        />
      </summary>
      <div className="border-border-subtle bg-surface-card rounded-field shadow-pop absolute z-10 mt-2 flex max-h-64 min-w-48 flex-col gap-1 overflow-y-auto border p-3">
        {valores.length === 0 && (
          <span className="type-sm text-text-muted">Nada para filtrar nesta página.</span>
        )}
        {valores.map((valor) => (
          <label key={valor} className="flex items-center gap-2 type-sm text-text-body">
            <input
              type="checkbox"
              checked={selecionados.includes(valor)}
              onChange={(evento) =>
                aoMudar(
                  evento.target.checked
                    ? [...selecionados, valor]
                    : selecionados.filter((v) => v !== valor),
                )
              }
            />
            {rotularValor ? rotularValor(valor) : valor}
          </label>
        ))}
        {ativo && (
          <button
            type="button"
            className="type-sm text-text-link hover:text-iox-teal mt-1 self-start"
            onClick={() => aoMudar([])}
          >
            limpar
          </button>
        )}
      </div>
    </details>
  )
}

/** O `⚙ colunas exibidas ▾` do shot `2g`, reaproveitado aqui. */
function SeletorDeColunas({
  ocultas,
  aoMudar,
}: {
  ocultas: Set<IdDeColuna>
  aoMudar: (proximo: Set<IdDeColuna>) => void
}) {
  return (
    <details className="relative inline-block">
      <summary className="rounded-control border-border-subtle bg-surface-card text-text-primary cursor-pointer list-none border px-4 py-2 type-button">
        <i className="ph ph-columns mr-2" aria-hidden="true" />
        colunas exibidas
      </summary>
      <div className="border-border-subtle bg-surface-card rounded-field shadow-pop absolute right-0 z-10 mt-2 flex min-w-56 flex-col gap-1 border p-3">
        {COLUNAS.filter((c) => c.rotulo).map((coluna) => (
          <label key={coluna.id} className="flex items-center gap-2 type-sm text-text-body">
            <input
              type="checkbox"
              checked={!ocultas.has(coluna.id)}
              onChange={(evento) => {
                const proximo = new Set(ocultas)
                if (evento.target.checked) proximo.delete(coluna.id)
                else proximo.add(coluna.id)
                aoMudar(proximo)
              }}
            />
            {coluna.rotulo}
          </label>
        ))}
      </div>
    </details>
  )
}
